import React, { useEffect, useState, useMemo } from "react";
import {
  Alert,
  StyleSheet,
  View,
  Modal,
  TouchableOpacity,
  TextInput,
} from "react-native";
import { scanCrate } from "@services/api";
import BarcodeScanner from "react-native-scan-barcode";
import { check, PERMISSIONS, RESULTS, request } from "react-native-permissions";
import HeaderWithBackAndTitle from "@components/HeaderWithBackAndTitle";
import CustomText from "@components/CustomText";
import Cross from "@assets/svg/Cross";
import LinearGradient from "react-native-linear-gradient";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { colors } from "@constants/theme";
const CrateScanner = ({ navigation, route }) => {
  const insets = useSafeAreaInsets();
  const [crateNo, setCrateNo] = useState("");
  const [modalVisible, setModalVisible] = useState(false);
  const [crateData, setCrateData] = useState({
    crateId: "",
    routeName: "",
    dbName: "",
  });
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(true);
  const isRouteInfoScreen = useMemo(() => {
    return route?.params?.type === "ROUTE_INFO";
  }, [route?.params?.type]);
  const switchState = route?.params?.type;

  const requestCameraPermission = async () => {
    try {
      await request(PERMISSIONS.ANDROID.CAMERA);
    } catch (error) {
      console.log(error);
    }
  };

  const checkCameraPermission = async () => {
    try {
      const result = await check(PERMISSIONS.ANDROID.CAMERA);
      if (result !== RESULTS.GRANTED) {
        await requestCameraPermission();
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    checkCameraPermission();
  }, []);

  const closeModal = () => {
    setModalVisible(false);
    setCrateNo("");
    setShowBarcodeScanner(true);
  };

  const barcodeReceived = async ({ type, data }) => {
    setShowBarcodeScanner(false);
    setCrateNo(type === "QR_CODE" ? data : "");
    const resp =
      data &&
      (await scanCrate({
        crate_id: encodeURIComponent(data),
        type: switchState,
        payload: {
          rescheduledPayloadInfo: route?.params?.rescheduleddata,
        },
      }));
    if (resp && resp?.data?.errorCode === 400) {
      Alert.alert(
        "Error",
        resp?.data?.errorText,
        [
          {
            text: "OK",
            onPress: () => {
              setShowBarcodeScanner(true);
            },
          },
        ],
        { cancelable: false },
      );
    } else if (resp && resp.data) {
      if (resp.data.navigate) {
        const crate = {
          next_payload: resp.data.next_payload,
          type: resp?.data?.next_payload?.type,
        };
        navigation.replace("CrateCustomerList", {
          crate,
          type: switchState,
          spoke: route?.params?.spoke,
          date: route?.params?.date,
        });
      } else if (resp.data.show_modal) {
        setCrateData({
          crateId: resp.data.next_payload.crate_id || "",
          routeName: resp.data.next_payload.route_name || "",
          dbName: resp.data.next_payload.db_name || "",
        });
        setModalVisible(true);
      }
    } else {
      setCrateNo("");
      setShowBarcodeScanner(true);
    }
  };

  // Handle manual submit using the same flow as barcode scan
  const handleSubmit = () => {
    if (!crateNo) return;
    // Re-use the same handler so the logic stays in one place
    barcodeReceived({ type: "QR_CODE", data: crateNo });
  };

  return (
    <View style={styles.container}>
      <HeaderWithBackAndTitle
        headertitle={isRouteInfoScreen ? "Route Details Scan" : "Scan Crate"}
        headerColor="#fff"
        hideShadow={false}
        navigation={navigation}
        route={route}
      />

      {/* Manual entry of crate number */}
      {isRouteInfoScreen && (
        <View style={[styles.inputWrapper]}>
          <TextInput
            style={styles.input}
            placeholder="Enter crate number"
            placeholderTextColor="#999"
            value={crateNo}
            onChangeText={setCrateNo}
            autoCapitalize="none"
            autoCorrect={false}
          />
          <TouchableOpacity style={styles.submitButton} onPress={handleSubmit}>
            <CustomText style={styles.submitButtonText}>Submit</CustomText>
          </TouchableOpacity>
        </View>
      )}

      {showBarcodeScanner && (
        <BarcodeScanner
          onBarCodeRead={barcodeReceived}
          style={styles.scanner}
          cameraType="back"
        />
      )}

      <Modal
        animationType="fade"
        transparent={true}
        visible={modalVisible}
        onRequestClose={closeModal}
        statusBarTranslucent
        navigationBarTranslucent
      >
        <View style={styles.modalBackground}>
          <View
            style={[styles.modalContainer, { paddingBottom: insets.bottom }]}
          >
            <View style={styles.modalHeader}>
              <CustomText variant="subtitle_16" style={styles.modalTitle}>
                Route Info
              </CustomText>
              <TouchableOpacity onPress={closeModal} style={styles.closeButton}>
                {Cross ? (
                  <Cross width={16} height={16} />
                ) : (
                  <CustomText>✕</CustomText>
                )}
              </TouchableOpacity>
            </View>

            <View style={styles.modalContent}>
              <CustomText style={styles.crateLabel}>
                Crate No: {crateData?.crateId || ""}
              </CustomText>
              <LinearGradient
                colors={["#212121", "#5f5f5f"]}
                start={{ x: 0.0, y: 0 }}
                end={{ x: 0.0, y: 1 }}
                style={styles.routeContainer}
              >
                <CustomText style={styles.routeLabel}>
                  {crateData?.routeName || ""}
                </CustomText>
              </LinearGradient>

              <CustomText style={styles.dbLabel}>
                DB Name: {crateData?.dbName || ""}
              </CustomText>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

export default CrateScanner;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.65)",
    position: "relative",
  },
  scanner: {
    height: "100%",
    width: "100%",
    backgroundColor: "transparent",
    position: "absolute",
    top: 0,
    left: 0,
    zIndex: -1,
  },
  modalBackground: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.2)",
    justifyContent: "flex-end",
    alignItems: "center",
    // zIndex: 5,
  },
  modalContainer: {
    width: "100%",
    backgroundColor: "#FFFFFF",
    borderRadius: 10,
    overflow: "hidden",
    elevation: 5,
  },
  modalHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#EEEEEE",
  },
  modalTitle: {
    fontSize: 16,
    fontWeight: "bold",
  },
  closeButton: {
    padding: 4,
  },
  modalContent: {
    padding: 10,
    alignItems: "center",
  },
  crateLabel: {
    fontSize: 16,
    marginBottom: 12,
  },
  routeContainer: {
    backgroundColor: "#333333",
    paddingVertical: 16,
    paddingHorizontal: 40,
    borderRadius: 12,
    marginBottom: 12,
  },
  routeLabel: {
    color: "#FFFFFF",
    fontSize: 24,
    fontWeight: "bold",
  },
  dbLabel: {
    fontSize: 16,
  },
  /* New styles for manual input */
  inputWrapper: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 10,
    paddingHorizontal: 16,
    width: "100%",
    backgroundColor: "white",
  },
  input: {
    flex: 1,
    height: 40,
    borderWidth: 1,
    borderColor: "#000",
    borderRadius: 8,
    paddingHorizontal: 8,
    backgroundColor: "#FFFFFF",
    color: "#000000",
  },
  submitButton: {
    marginLeft: 8,
    backgroundColor: colors.plain_green,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
  },
  submitButtonText: {
    color: "#FFFFFF",
    fontWeight: "bold",
  },
});
